import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Calendar, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const HeroSectionImproved = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30, clipPath: 'inset(100% 0 0 0)' },
    visible: {
      opacity: 1,
      y: 0,
      clipPath: 'inset(0% 0 0 0)',
      transition: {
        duration: 1,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="hero" ref={sectionRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Enhanced Parallax Background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 z-0"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1618418721668-0d1f72aa4bab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjYXJ8ZW58MHx8fGJsYWNrfDE3ODUxMTMzMDR8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        {/* Stronger blue overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/98 via-[#0A1628]/90 to-[#0A1628]" />
        
        {/* Additional atmospheric overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 via-transparent to-[#0A1628]/80" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 max-w-7xl mx-auto px-6 text-center pt-20"
      >
        {/* Logo with enhanced entrance */}
        <motion.div 
          variants={textVariants} 
          className="mb-10 flex justify-center"
        >
          <motion.img 
            src="/lavaporcar-logo.png" 
            alt="Lavaporcar Logo" 
            className="h-36 md:h-48 w-auto drop-shadow-2xl"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>

        {/* Enhanced Slogan with better typography */}
        <motion.div variants={textVariants} className="mb-8">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight tracking-tight">
            Pasión por tu vehículo,
            <br />
            <span className="text-[#60A5FA] drop-shadow-[0_0_30px_rgba(96,165,250,0.5)]">
              excelencia en cada detalle.
            </span>
          </h1>
        </motion.div>

        {/* Enhanced Description */}
        <motion.div variants={textVariants} className="mb-14 max-w-3xl mx-auto">
          <p className="text-xl md:text-2xl lg:text-3xl text-white/90 leading-relaxed font-light">
            Somos especialistas en estética automotriz con 3 años de experiencia en el mercado.
            Utilizamos productos y técnicas de alta calidad para superar las expectativas de nuestros clientes.
          </p>
        </motion.div>

        {/* Enhanced CTA Buttons with better visual hierarchy */}
        <motion.div variants={textVariants} className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <Button
            size="lg"
            className="bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-white hover:from-[#3B82F6] hover:to-[#2563EB] text-xl px-10 py-8 rounded-2xl transition-all duration-300 hover:scale-110 hover:shadow-[0_0_40px_rgba(96,165,250,0.6)] border-2 border-[#60A5FA]/30"
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
          >
            <Calendar className="w-6 h-6 mr-3" />
            Reserva tu cita ahora
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-3 border-white/40 text-white hover:bg-white/10 hover:border-white text-xl px-10 py-8 rounded-2xl transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            onClick={() => window.open('https://wa.me/593958646225', '_blank')}
          >
            <MessageCircle className="w-6 h-6 mr-3" />
            WhatsApp: 0958646225
          </Button>
        </motion.div>

        {/* Enhanced Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-7 h-12 border-3 border-white/50 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2 h-2 bg-white/80 rounded-full shadow-lg"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSectionImproved;
